<script setup lang="ts">
import type { MessageCard, ConfirmableItem } from '~/types/web-chat'

/**
 * The ONE place a confirm client tool's card becomes a component.
 *
 * Was eight `v-if` branches in `ChatMessages.vue`, each reading its own `msg.<tool>Request` field and
 * emitting its own tool-specific accept/decline pair — so adding a confirm tool meant editing the
 * message type, that template, and the panel's handlers, and forgetting any of them failed silently.
 * Now: one `card` in, `accept` / `decline` / `download` out.
 *
 * The child components keep their own prop and emit names; mapping them here is deliberate, so the
 * collapse costs no churn in six card components that are otherwise fine.
 */
defineProps<{ card: MessageCard }>()

const emit = defineEmits<{
  accept: []
  decline: []
  download: [item: ConfirmableItem]
  /** `run_workflow` only: hand the prefilled values to the full run page (MAR-437). */
  'open-form': []
}>()
</script>

<template>
  <!-- Storage mutations: the agent must learn the real outcome, so these resume on the decision. -->
  <ChatDeleteConfirmCard
    v-if="card.toolId === 'delete_files'"
    :request="card.request"
    @confirm-delete="emit('accept')"
    @cancel-delete="emit('decline')"
  />

  <ChatMoveConfirmCard
    v-else-if="card.toolId === 'move_files'"
    :request="card.request"
    @confirm-move="emit('accept')"
    @cancel-move="emit('decline')"
  />

  <ChatRenameConfirmCard
    v-else-if="card.toolId === 'rename_files'"
    :request="card.request"
    @confirm-rename="emit('accept')"
    @cancel-rename="emit('decline')"
  />

  <ChatCreateFolderConfirmCard
    v-else-if="card.toolId === 'create_folder'"
    :request="card.request"
    @confirm-create-folder="emit('accept')"
    @cancel-create-folder="emit('decline')"
  />

  <!-- Filing to a case LINKS the documents (MAR-988); the files do not move. -->
  <ChatFileToCaseConfirmCard
    v-else-if="card.toolId === 'file_to_case'"
    :request="card.request"
    @confirm-file-to-case="emit('accept')"
    @cancel-file-to-case="emit('decline')"
  />

  <!-- The agent asking to lift the chat's document scope (§6b) — it cannot widen scope itself. -->
  <ChatClearScopeCard
    v-else-if="card.toolId === 'clear_chat_scope'"
    :request="card.request"
    @confirm-clear-scope="emit('accept')"
    @cancel-clear-scope="emit('decline')"
  />

  <ChatWorkflowCard
    v-else-if="card.toolId === 'run_workflow'"
    :request="card.request"
    @accept-workflow="emit('accept')"
    @decline-workflow="emit('decline')"
    @open-workflow-form="emit('open-form')"
  />

  <ChatConvertDocumentCard
    v-else-if="card.toolId === 'convert_document'"
    :request="card.request"
    @confirm-convert="emit('accept')"
    @cancel-convert="emit('decline')"
  />

  <!-- Offer cards: no accept/decline. The session already resumed with an ack when the card
       rendered (`resume: 'immediate'`); what the user clicks here runs its own flow. -->
  <SearchResultsCard
    v-else-if="card.toolId === 'present_document_results'"
    :card="card.request"
  />

  <ChatDownloadDocumentsCard
    v-else-if="card.toolId === 'download_documents'"
    :request="card.request"
    @download-document="emit('download', $event)"
  />
</template>
